import { z } from 'zod';
import { MOTIVOS_REJEICAO_INDICACAO, type MotivoRejeicaoIndicacao } from './erros.js';

/**
 * Contrato do dashboard do painel interno (SPEC §8 "Métricas", UX-PAINEL-INTERNO
 * §8). Uma única rota agregada `GET /v1/metricas?de=&ate=` — toda a agregação
 * roda no banco; o front nunca soma listas (`CLAUDE.md` regra 1).
 *
 * Sem `de`/`ate` = campanha inteira. Datas ISO 8601.
 */

export const metricasQuerySchema = z.object({
  de: z.coerce.date().optional(),
  ate: z.coerce.date().optional(),
});
export type MetricasQuery = z.infer<typeof metricasQuerySchema>;

export const rankingIndicadorSchema = z.object({
  clienteId: z.string().uuid(),
  nome: z.string(),
  primeiroNome: z.string(),
  conversoes: z.number().int(),
});
export type RankingIndicador = z.infer<typeof rankingIndicadorSchema>;

/** `{ [motivo]: n }` para os 8 motivos — sempre os 8, com 0 onde não houve. */
export type RejeicoesPorMotivo = Record<MotivoRejeicaoIndicacao, number>;

export const rejeicoesPorMotivoSchema: z.ZodType<RejeicoesPorMotivo> = z.object(
  Object.fromEntries(MOTIVOS_REJEICAO_INDICACAO.map((m) => [m, z.number().int()])),
) as unknown as z.ZodType<RejeicoesPorMotivo>;

export const metricasRespostaSchema = z.object({
  periodo: z.object({
    de: z.string().nullable(),
    ate: z.string().nullable(),
  }),
  /** conversões no período por `convertidaEm` */
  indicacoesConvertidas: z.number().int(),
  /** rejeições no período por `criadoEm` */
  indicacoesRejeitadas: z.number().int(),
  /** convertidas / (convertidas + rejeitadas) — 0 quando não há indicações */
  taxaConversao: z.number(),
  rejeicoesPorMotivo: rejeicoesPorMotivoSchema,
  rankingIndicadores: z.array(rankingIndicadorSchema),
  /** concessões de `movimento_brinde` no período */
  brindesConcedidos: z.number().int(),
  /** entregas de `movimento_brinde` no período */
  brindesEntregues: z.number().int(),
  /** entregues / concedidos no período — aproximação (a entrega pode ser de uma concessão anterior) */
  taxaRetirada: z.number(),
  /** concessões sem entrega há mais de 30 dias (ponto no tempo, não recortado por período) */
  vouchersParados: z.number().int(),
  /**
   * soma de `custo_snapshot` das concessões no período. `null` quando nenhuma
   * concessão do período tem custo preenchido (o dono ainda não lançou os valores
   * dos brindes — O1). `custoConcedidoParcial` avisa que parte das concessões
   * não tem custo.
   */
  custoConcedido: z.number().nullable(),
  custoConcedidoParcial: z.boolean(),
  /**
   * novos clientes `origem='indicacao'` no período ÷ indicadores distintos com
   * conversão no período. `null` quando não houve indicadores no período.
   * **Aproximação** (parte dos indicados viria sem o programa — O4).
   */
  kFactor: z.number().nullable(),
});
export type MetricasResposta = z.infer<typeof metricasRespostaSchema>;
